import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { AppConfig } from '@config/app.config';
import { AmlLimitsService } from '@core/services/aml-limits.service';
import { GeneralsService } from '@core/services/generals.service';
import { AmlLimitsEditComponent } from '@modules/aml-limits/aml-limits-edit/aml-limits-edit.component';
import { Constants } from '@shared/class/constants.class';
import { AmlLimit } from '@shared/interfaces/aml-limit';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-aml-limits-list',
  templateUrl: './aml-limits-list.component.html'
})
export class AmlLimitsListComponent implements OnInit, OnDestroy {
  public constants = new Constants();

  public location = 'limits';
  public amlLimitsList: Array<AmlLimit> = [];
  public amlLimit: AmlLimit;
  public dataSource: MatTableDataSource<AmlLimit>;
  public selection = new SelectionModel<AmlLimit>(true, []);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filter') filterInput: ElementRef;

  constructor(
    public appConfig: AppConfig,
    public generalsService: GeneralsService,
    public amlLimitsService: AmlLimitsService
  ) {
    this.constants.displayedColumns = [
      this.appConfig.ACTIONS.delete.id,
      'id',
      'segment',
      'idSegment',
      'monthlyValue',
      'monthlyCount',
      'annualValue',
      'annualCount',
      this.appConfig.ACTIONS.edit.id
    ];
  }

  ngOnInit() {
    this.generalsService.action$
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((result: any) => {
        if (result) {
          const actions = this.generalsService.getActions(
            this.constants.displayedColumns,
            this.constants.actionsList,
            this.location
          );
          this.constants.displayedColumns = actions.displayedColumns;
          this.constants.displayedActions = actions.displayedActions;
          this.loadData();
        }
      });
  }

  ngOnDestroy() {
    this.constants.destroy$.next(true);
    this.constants.destroy$.unsubscribe();
  }

  loadData() {
    this.constants.loading = true;
    this.constants.elements = true;
    this.amlLimitsService
      .getItems()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          this.amlLimitsList = [];
          result.data.forEach((element: any) => {
            const elementAmlLimit = element.attributes;
            const elementSegment = element.relationships.segment;
            this.amlLimit = {
              id: element.id,
              segment: elementSegment.name,
              idSegment: elementSegment.id,
              monthlyCount: elementAmlLimit.monthlyCount,
              monthlyValue: elementAmlLimit.monthlyValue,
              annualCount: elementAmlLimit.annualCount,
              annualValue: elementAmlLimit.annualValue
            };
            this.amlLimitsList.push(this.amlLimit);
          });
          if (this.amlLimitsList.length) {
            this.dataSource = new MatTableDataSource(this.amlLimitsList);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.constants.filter = this.dataSource.filteredData;
            this.selection.clear();
          } else {
            this.constants.elements = false;
          }

          this.constants.error = false;
          this.constants.loading = false;
        },
        error => {
          this.constants.errorText = this.generalsService.showError(error);
          this.constants.error = true;
          this.constants.loading = false;
        }
      );
  }

  reload() {
    this.generalsService.reload(this);
  }

  edit(action: string, item?: AmlLimit) {
    let dialog: any;

    const data = {
      action: action,
      item: {},
      relationships: {}
    };

    const loading = this.generalsService.loading();

    this.constants.message = '';

    this.amlLimitsService
      .segmentWithoutAmlLimits()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          let res = false;
          loading.close();
          if (!result.data || result.data.length <= 0) {
            res = true;
          }

          if (action === this.appConfig.ACTIONS.add.id && res) {
            this.generalsService.notification(
              this.appConfig.NOTIFICATION.error,
              this.appConfig.LANG.amlLimits.noSegments
            );
          } else {
            if (action === this.appConfig.ACTIONS.add.id) {
              this.constants.message = this.appConfig.LANG.message.addSuccess;
            } else {
              data.item = item;
              this.constants.message = this.appConfig.LANG.message.editSuccess;
            }

            data.relationships = result.data;

            dialog = this.generalsService.modal(AmlLimitsEditComponent, data);

            dialog
              .afterClosed()
              .pipe(takeUntil(this.constants.destroy$))
              .subscribe((afterClosedResult: any) => {
                this.generalsService.afterClosed(
                  this,
                  afterClosedResult,
                  this.constants.message
                );
              });
          }
        },
        error => {
          loading.close();
          this.generalsService.notification(
            this.appConfig.NOTIFICATION.error,
            this.generalsService.showError(error)
          );
        }
      );
  }

  selectDelete() {
    const numSelected = this.selection.selected.length,
      numRows = this.dataSource.data.length;

    this.constants.labelDelete = this.generalsService.messageSelectedDelete(
      'label',
      numSelected,
      numRows
    );
  }

  filterData(event: any) {
    this.generalsService.applyFilter(this.dataSource, event);
    this.constants.filter = this.dataSource.filteredData;
  }
}
